import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, Search, X } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useAccount } from '../../context/AccountContext';
import {
  GET_COLLECTION_ITEMS,
  GET_COLLECTION_FACETS,
  GET_COLLECTION_PORTFOLIO,
} from '../../graphql';
import { Loader } from '../common/PageLoader';

/**
 * The Collect shell's home: the collection as a grid.
 *
 * A collection is looked at, not read — the photograph is the content — so this
 * leads with the image and keeps text to what identifies the item.
 *
 * Filtering, faceting and pagination all happen server-side. An earlier pass
 * fetched every item and filtered in the browser, which works at a few hundred
 * items and falls over well before a serious collection does.
 */

const PAGE_SIZE = 48;

interface Listing {
  askingPrice: number | null;
  currency: string | null;
  status: string;
  soldPrice: number | null;
}

interface CollectionItem {
  id: string;
  sku: string;
  title: string | null;
  mediaUrl: string | null;
  publishStatus: string;
  customFields: Record<string, unknown> | null;
  taxonomies: Record<string, { term: string }[]> | null;
  listing: Listing | null;
}

interface Facet {
  dimension: string;
  terms: { term: string; count: number }[];
}

/** GraphQL arg name for each facet dimension (product_set → productSet). */
const FILTER_ARG: Record<string, string> = {
  sport: 'sport',
  league: 'league',
  manufacturer: 'manufacturer',
  product_set: 'productSet',
  team: 'team',
  player: 'player',
};

const DIMENSION_LABEL: Record<string, string> = {
  sport: 'Sport',
  league: 'League',
  manufacturer: 'Manufacturer',
  product_set: 'Set',
  team: 'Team',
  player: 'Player',
};

const term = (t: CollectionItem['taxonomies'], key: string) => t?.[key]?.[0]?.term ?? '';

const field = (f: CollectionItem['customFields'], key: string) => {
  const v = f?.[key];
  return v === undefined || v === null ? '' : String(v);
};

const money = (n: number | null | undefined, currency = 'USD') =>
  n == null ? null : new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);

export function CollectionLayout() {
  const { accountId, isLoading: accountsLoading } = useAccount();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<string>('');
  const [facetFilters, setFacetFilters] = useState<Record<string, string>>({});
  const [offset, setOffset] = useState(0);

  // Facet selections map onto named GraphQL args, so only the dimensions the
  // schema knows about can ever reach the server.
  const filterVars = useMemo(() => {
    const vars: Record<string, string> = {};
    for (const [dimension, value] of Object.entries(facetFilters)) {
      const arg = FILTER_ARG[dimension];
      if (arg && value) vars[arg] = value;
    }
    return vars;
  }, [facetFilters]);

  const { data, loading, error } = useQuery(GET_COLLECTION_ITEMS, {
    variables: {
      accountId,
      q: query.trim() || null,
      status: status || null,
      limit: PAGE_SIZE,
      offset,
      ...filterVars,
    },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const { data: facetData } = useQuery(GET_COLLECTION_FACETS, {
    variables: { accountId },
    skip: !accountId,
  });

  const { data: pfData } = useQuery(GET_COLLECTION_PORTFOLIO, {
    variables: { accountId },
    skip: !accountId,
  });

  const page = data?.collectionItems;
  const items: CollectionItem[] = page?.items ?? [];
  const facets: Facet[] = facetData?.collectionFacets ?? [];
  const portfolio = pfData?.collectionPortfolio;

  const setFacet = (dimension: string, value: string) => {
    setOffset(0);
    setFacetFilters((prev) => {
      const next = { ...prev };
      if (!value || next[dimension] === value) delete next[dimension];
      else next[dimension] = value;
      return next;
    });
  };

  const activeFilters = Object.entries(facetFilters);
  const clearAll = () => {
    setFacetFilters({});
    setStatus('');
    setQuery('');
    setOffset(0);
  };

  if (accountsLoading) return <Loader label="Loading" className="p-6 text-cmp-ink-3" />;

  return (
    <div className="ink-page flex w-full flex-col h-full min-h-0 overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
        <div className="w-full max-w-full overflow-hidden relative flex-1 flex flex-col">
          <div className="m-0 w-full h-full relative overflow-hidden box-border bg-primary-background flex flex-col">
            <div className="ink-frame">
              <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1">
                <header className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h1 className="text-2xl font-semibold text-cmp-ink flex items-center gap-2">
                      <LayoutGrid className="w-6 h-6" /> Collection
                    </h1>
                    {portfolio && (
                      <p className="text-sm text-cmp-ink-2 mt-1">
                        {portfolio.cards} item{portfolio.cards === 1 ? '' : 's'}
                        {portfolio.askingTotal > 0 && <> · {money(portfolio.askingTotal)} asking</>}
                        {portfolio.sold > 0 && (
                          <>
                            {' '}
                            · {portfolio.sold} sold for {money(portfolio.soldTotal)}
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-cmp-ink-3" />
                      <Input
                        value={query}
                        onChange={(e) => {
                          setQuery(e.target.value);
                          setOffset(0);
                        }}
                        placeholder="Search player, title, sku…"
                        className="pl-8 w-64"
                      />
                    </div>
                    {[
                      { v: '', label: 'All' },
                      { v: 'draft', label: 'Draft' },
                      { v: 'published', label: 'Published' },
                    ].map((s) => (
                      <button
                        key={s.v || 'all'}
                        onClick={() => {
                          setStatus(s.v);
                          setOffset(0);
                        }}
                        className={`px-3 py-1.5 rounded text-sm ${
                          status === s.v
                            ? 'bg-cmp-bronze-soft text-cmp-ink'
                            : 'text-cmp-ink-3 hover:text-cmp-ink'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </header>

                {activeFilters.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {activeFilters.map(([dimension, value]) => (
                      <button
                        key={dimension}
                        onClick={() => setFacet(dimension, '')}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-cmp-bronze-soft text-cmp-ink text-xs"
                      >
                        {DIMENSION_LABEL[dimension] || dimension}: {value}
                        <X className="w-3 h-3" />
                      </button>
                    ))}
                    <button
                      onClick={clearAll}
                      className="text-xs text-cmp-ink-2 underline hover:text-cmp-ink"
                    >
                      clear all
                    </button>
                  </div>
                )}

                <div className="flex gap-6 items-start">
                  {/* Facets */}
                  <aside className="w-56 shrink-0 space-y-4 hidden lg:block">
                    {facets.map((f) => (
                      <Card
                        key={f.dimension}
                        className="p-3 bg-cmp-raise border border-cmp-line text-cmp-ink"
                      >
                        <div className="text-xs font-medium text-cmp-ink mb-2">
                          {DIMENSION_LABEL[f.dimension] || f.dimension}
                        </div>
                        <div className="space-y-1 max-h-56 overflow-auto pr-1">
                          {f.terms.slice(0, 40).map((t) => {
                            const active = facetFilters[f.dimension] === t.term;
                            return (
                              <button
                                key={t.term}
                                onClick={() => setFacet(f.dimension, t.term)}
                                className={`w-full flex items-center justify-between text-left text-xs px-1.5 py-1 rounded ${
                                  active
                                    ? 'bg-cmp-bronze-soft text-cmp-ink'
                                    : 'text-cmp-ink-3 hover:text-cmp-ink hover:bg-cmp-bronze-soft/50'
                                }`}
                              >
                                <span className="truncate">{t.term}</span>
                                <span className="tabular-nums opacity-70 ml-2">{t.count}</span>
                              </button>
                            );
                          })}
                        </div>
                      </Card>
                    ))}
                  </aside>

                  {/* Grid */}
                  <div className="flex-1 min-w-0 space-y-4">
                    {error && (
                      <Card className="p-4 text-sm text-red-400 bg-cmp-raise border border-cmp-line text-cmp-ink">
                        Could not load collection: {error.message}
                      </Card>
                    )}

                    {!loading && items.length === 0 && (
                      <Card className="p-10 text-center text-cmp-ink-2 bg-cmp-raise border border-cmp-line text-cmp-ink">
                        <LayoutGrid className="w-[2rem] h-[2rem] mx-auto mb-3 opacity-50" />
                        <p>
                          {activeFilters.length || query || status
                            ? 'Nothing matches those filters.'
                            : 'Nothing here yet.'}
                        </p>
                        {!activeFilters.length && !query && !status && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-4"
                            onClick={() => navigate('/capture')}
                          >
                            Start capturing
                          </Button>
                        )}
                      </Card>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
                      {items.map((it) => {
                        const player = field(it.customFields, 'player_name');
                        const year = field(it.customFields, 'year');
                        const number = field(it.customFields, 'card_number');
                        const grader = field(it.customFields, 'grader');
                        const grade = field(it.customFields, 'grade');
                        const price = money(it.listing?.askingPrice, it.listing?.currency || 'USD');
                        return (
                          <Card
                            key={it.id}
                            onClick={() => navigate(`/collection/${it.id}`)}
                            className="overflow-hidden cursor-pointer bg-cmp-raise border border-cmp-line text-cmp-ink hover:ring-1 hover:ring-cmp-bronze transition"
                          >
                            <div className="aspect-[3/4] bg-cmp-surface flex items-center justify-center overflow-hidden">
                              {it.mediaUrl ? (
                                <img
                                  src={it.mediaUrl}
                                  alt={it.title || 'item'}
                                  loading="lazy"
                                  className="w-full h-full object-contain"
                                />
                              ) : (
                                <LayoutGrid className="w-6 h-6 opacity-40" />
                              )}
                            </div>
                            <div className="p-2 space-y-1">
                              <div className="text-xs text-cmp-ink truncate" title={it.title || ''}>
                                {player || it.title || 'Untitled'}
                              </div>
                              <div className="text-[11px] text-cmp-ink-2 truncate">
                                {[year, term(it.taxonomies, 'manufacturer'), number && `#${number}`]
                                  .filter(Boolean)
                                  .join(' ')}
                              </div>
                              <div className="flex items-center gap-1 pt-0.5 flex-wrap">
                                {it.publishStatus === 'published' ? (
                                  <Badge className="text-[10px]">published</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[10px]">
                                    draft
                                  </Badge>
                                )}
                                {grader && grader !== 'Raw' && (
                                  <Badge variant="outline" className="text-[10px]">
                                    {grader} {grade}
                                  </Badge>
                                )}
                                {it.listing?.status === 'sold' ? (
                                  <Badge variant="outline" className="text-[10px]">
                                    sold {money(it.listing.soldPrice)}
                                  </Badge>
                                ) : (
                                  price && (
                                    <span className="text-[11px] text-cmp-ink-2">{price}</span>
                                  )
                                )}
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>

                    {page && page.total > PAGE_SIZE && (
                      <div className="flex items-center justify-between text-sm text-cmp-ink-2">
                        <span>
                          {offset + 1}–{Math.min(offset + PAGE_SIZE, page.total)} of {page.total}
                        </span>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={offset === 0}
                            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={offset + PAGE_SIZE >= page.total}
                            onClick={() => setOffset(offset + PAGE_SIZE)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
